import React, { useState, useEffect } from "react";
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Badge,
  Avatar,
  styled,
  Paper,
  Fade,
  Slide,
  useTheme,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  keyframes,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Category as CategoryIcon,
  AddCircle as AddCircleIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import UsersList from "./UsersList";
import Categories from "./ProductCategories";
import AddProduct from "./AddProduct";
import DashboardOverview from "./DashboardOverview";
import LoginForm from "./LoginForm";
import { useDispatch } from "react-redux";
import { logout } from "../redux/slices/authSlice";
import { useSelector } from "react-redux";
import { restoreAuth } from "../redux/slices/authSlice";
import CategorySettingsAdmin from "./CategorySettingsAdmin";
// Custom theme with red shades
const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;
const AnimatedButton = styled(Button)({
  animation: `${pulseAnimation} 3s ease infinite`,
});
const theme = createTheme({
  palette: {
    primary: {
      main: "#b71c1c", // Deep red
      light: "#f28b82", // Light red for hover/selection
      dark: "#7f0000", // Darker red
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#ff8a80", // Soft red tone
      contrastText: "#000000",
    },
    background: {
      default: "#fff5f5", // Very light red tint
      paper: "#ffffff",
    },
    text: {
      primary: "#4e0000", // Strong red text
      secondary: "#7f0000", // Softer red
    },
    action: {
      hover: "#ffe0e0", // Light red on hover
      selected: "#ffcdd2",
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
  },
});
const drawerWidth = 200;
const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
       marginLeft: 0, // Always no margin on mobile
    },
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
      marginLeft: `-${drawerWidth}px`,
    //marginLeft: `-${drawerWidth}px`,
    ...(open && {
      //marginLeft: `${drawerWidth}px`,
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
    variants: [
      {
        props: ({ open }) => open,
        style: {
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }),
          marginLeft: 0,
        },
      },
    ],
    background: "linear-gradient(135deg, #fff0f0 0%, #ffd6d6 100%)",
    minHeight: "calc(100vh - 0px)",
    // maxHeight: "calc(100vh - 73px)",       // 🤖 cap height
    overflowY: "auto", // → Allows vertical scrolling
    position: "relative",
    overflowX: "hidden",
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#d32f2f",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#ffe0e0",
    },

    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage:
        'url("https://as1.ftcdn.net/v2/jpg/12/30/04/06/1000_F_1230040616_wR6YlhSjXdt0oNAbO0aSDOV1gihbYl2r.jpg")',
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      opacity: 0.1,
      pointerEvents: "none",
    },
  })
);

const AppBarStyled = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
    variants: [
    {
      props: ({ open }) => open,
      style: {
        width: `calc(100% - ${drawerWidth}px)`,
        marginLeft: `${drawerWidth}px`,
        transition: theme.transitions.create(['margin', 'width'], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  backgroundColor: "#ffffff",
  color: theme.palette.text.primary,
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
  borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
}));
const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 0),
  minHeight: "73px",
  justifyContent: "space-between",
   ...theme.mixins.toolbar,
  //background: 'linear-gradient(135deg, #8e0000 0%, #b71c1c 100%)',
  color: "#4e0000",
}));
const AnimatedListItem = styled(ListItem)(({ theme, selected }) => ({
  borderRadius: theme.shape.borderRadius,
  margin: theme.spacing(0.5, 1),
  padding: theme.spacing(1, 2),
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#ffe0e0", // soft red hover
    transform: "translateX(0px)",
  },
  ...(selected && {
    backgroundColor: "#ffcdd2 !important",
    color: "#b71c1c",
    "& .MuiListItemIcon-root": {
      color: "#b71c1c",
    },
  }),
}));
const ContentPaper = styled(Paper)(({ theme }) => ({
  borderRadius: "16px",
  padding: theme.spacing(4),
  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 15px 35px rgba(0, 0, 0, 0.1)",
  },
  background: "rgba(255, 235, 238, 0.8)", // soft red paper
  backdropFilter: "blur(10px)",
}));
const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const user_type = useSelector((state) => state.auth.user_type);
  const loggedInemail = useSelector((state) => state.auth.email);
  const token = useSelector((state) => state.auth.token);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const dispatch = useDispatch();
  const [openLogoutAlert, setOpenLogoutAlert] = useState(false);
  const [activeTab, setActiveTab] = useState("users");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(!isMobile);
  const handleDrawerToggle = () => {
    setOpen(!open);
  };
  //Logout function
  const handleClose = () => setOpenLogoutAlert(false);
  const handleLogout = () => setOpenLogoutAlert(true);
  const handleLogoutConfirm = () => {
    dispatch(logout());
    setOpenLogoutAlert(false);
  };
  // Mobile menu state
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  // Mobile menu handlers
  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };
  //  useEffect(() => {
  //       const tokenLocal = localStorage.getItem("token");
  //       console.log(tokenLocal)
  //     if (tokenLocal) {
  //       setLoading(false);
  //       setIsAuthenticatedState(true);
  //     }
  //   }, []);
  useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);
  useEffect(() => {
    const storedAuth = localStorage.getItem("auth");
    const page = localStorage.getItem("page");
    if (page) {
      setActiveTab(page);
    }
    if (storedAuth) {
      try {
        const parsed = JSON.parse(storedAuth);
        if (parsed.token) {
          dispatch(restoreAuth(parsed));
        }
      } catch (err) {
        console.error("Failed to parse auth from localStorage", err);
        localStorage.removeItem("auth");
      }
    }
  }, [dispatch]);
  const menuItems = [
    // { id: 'dashboard', text: 'Dashboard', icon: <DashboardIcon /> },
    { id: "users", text: "Users", icon: <PeopleIcon /> },
    { id: "categories", text: "Categories", icon: <CategoryIcon /> },
    { id: "addProduct", text: "Add Product", icon: <AddCircleIcon /> },
    { id: "categorysettings", text: "Category Settings", icon: <SettingsIcon /> },
  ];
  const bottomMenuItems = [
    // { id: 'settings', text: 'Settings', icon: <SettingsIcon /> },
    { id: "logout", text: "Logout", icon: <LogoutIcon /> },
  ];
  if (!isAuthenticated) {
    // User is NOT logged in - show login form
    return <LoginForm />;
  }

  //  if (loading) return <div>Loading...</div>;
  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <AppBarStyled
          position="fixed"
          open={open && !isMobile}
          elevation={0}
          sx={{ height: 73 }}
        >
          {/* Mobile Menu */}
          <Menu
            anchorEl={mobileMenuAnchor}
            open={Boolean(mobileMenuAnchor)}
            onClose={handleMobileMenuClose}
            PaperProps={{
              sx: {
                borderRadius: "16px",
                minWidth: "250px",
                boxShadow: "0 10px 30px rgba(211, 47, 47, 0.2)",
                border: "1px solid rgba(211, 47, 47, 0.1)",
                mt: 1,
              },
            }}
          >
            <MenuItem dense sx={{ py: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    mr: 2,
                    backgroundColor: "#d32f2f",
                    color: "white",
                    fontSize: "1.1rem",
                  }}
                >
                  {loggedInemail.charAt(0).toUpperCase()}
                </Avatar>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 500, color: "#4e0000" }}
                >
                  {loggedInemail}
                </Typography>
              </Box>
            </MenuItem>
            <Divider sx={{ my: 0.5, borderColor: "rgba(211, 47, 47, 0.1)" }} />
            <MenuItem
              onClick={() => {
                handleMobileMenuClose();
                handleLogout();
              }}
              dense
              sx={{ py: 1.5 }}
            >
              <LogoutIcon sx={{ mr: 2, color: "#d32f2f" }} />
              <Typography
                variant="body1"
                sx={{ fontWeight: 500, color: "#4e0000" }}
              >
                Logout
              </Typography>
            </MenuItem>
          </Menu>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerToggle}
              edge="start"
              // sx={{ mr: 2, color: theme.palette.text.primary, }}
               sx={{mr: 2, color: "#d32f2f", ml: 1 }}
            >
              {open ? <ChevronLeftIcon /> : <MenuIcon />}
            </IconButton>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ flexGrow: 1, fontWeight: 600 }}
            >
              Admin Dashboard
            </Typography>
            {/* <IconButton color="inherit" sx={{ mr: 1 }}>
              <Badge badgeContent={4} color="error">
                <NotificationsIcon sx={{ color: theme.palette.text.secondary }} />
              </Badge>
            </IconButton> */}
            <Slide direction="left" in={!isMobile} mountOnEnter unmountOnExit>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexGrow: isMobile ? 0 : 1,
                  justifyContent: "flex-end",
                }}
              >
                <Typography variant="subtitle1" sx={{ mr: 1 }}>
                  {loggedInemail}
                </Typography>
                <Avatar
                  alt="Admin User"
                  src="/static/images/avatar/1.jpg"
                  sx={{
                    width: 36,
                    height: 36,
                    border: `2px solid ${theme.palette.primary.main}`,
                  }}
                />
                <AnimatedButton
                  variant="contained"
                  color="primary"
                  startIcon={<LogoutIcon />}
                  onClick={handleLogout}
                  sx={{
                    borderRadius: "12px",

                    fontSize: { xs: "0.6rem", md: "0.8rem" },
                  }}
                >
                  Logout
                </AnimatedButton>
              </Box>
            </Slide>
            {isMobile && (
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleMobileMenuOpen}
                sx={{ color: "#d32f2f", ml: 1 }}
              >
                <MoreVertIcon  />
              </IconButton>
            )}
          </Toolbar>
        </AppBarStyled>
        <Drawer
          variant={isMobile ? "temporary" : "persistent"}
          open={open}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
             // position: 'relative', // Add this
              // display: isMobile ?  'block' : 'clock',
              // backgroundColor: "#090909ff",
              // borderRight: "none",
              // boxShadow: "2px 0 10px rgba(0, 0, 0, 0.05)",
            },
          }}
          anchor="left"
          // open={open}
        >
          <DrawerHeader>
            {/* <Box sx={{justifyContent:"center",alignItems:"center"}}> */}
            <Avatar
              src="/Logo2.png"
              variant="square"
              sx={{
                width: "100%",
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
              }}
            />
            {/* <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Bilimbe
              </Typography> */}
            {/* </Box> */}
            {/* <IconButton onClick={handleDrawerToggle} sx={{ color: '#ffffff' }}>
              <ChevronLeftIcon />
            </IconButton> */}
          </DrawerHeader>
          <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.1)" }} />
          <List>
            {menuItems.map((item) => (
              <Fade in={open} key={item.id}>
                <AnimatedListItem
                  sx={{
                    minWidth: "20px",
                    backgroundColor: activeTab === item.id ? "" : "white",
                    margin: 0,
                  }}
                  button
                  onClick={() => {
                    setActiveTab(item.id);
                    localStorage.setItem("page", item.id);
                  }}
                  selected={activeTab === item.id}
                >
                  <ListItemIcon sx={{ minWidth: "40px" }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                  {activeTab === item.id && (
                    <ChevronRightIcon sx={{ fontSize: "1rem", ml: 0 }} />
                  )}
                </AnimatedListItem>
              </Fade>
            ))}
          </List>
          {/* <Box sx={{ mt: 'auto' }}>
            <Divider sx={{ my: 1 }} />
            <List>
              {bottomMenuItems.map((item) => (
                <Fade in={open} key={item.id}>
                  <AnimatedListItem button sx={{ minWidth: '30px',margin:0}} onClick={()=>(handleLogout())}>
                    <ListItemIcon sx={{ minWidth: '40px' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text} 
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </AnimatedListItem>
                </Fade>
              ))}
            </List>
          </Box> */}
        </Drawer>
        <Main
          open={open}
          // sx={{
          //   // ml: isMobile ? 0 : open ? 0: 0,
          //   transition: (theme) =>
          //     theme.transitions.create("margin", {
          //       easing: theme.transitions.easing.sharp,
          //       duration: theme.transitions.duration.leavingScreen,
          //     }),
          //   ...(open &&
          //     !isMobile && {
          //       transition: (theme) =>
          //         theme.transitions.create("margin", {
          //           easing: theme.transitions.easing.easeOut,
          //           duration: theme.transitions.duration.enteringScreen,
          //         }),
          //     }),
          // }}
        >
          <DrawerHeader />
          <Fade in={true} timeout={500}>
            <ContentPaper elevation={0}>
              {/* {activeTab === 'dashboard' && <DashboardOverview />} */}
              {activeTab === "users" && <UsersList />}
              {activeTab === "categories" && <Categories />}
              {activeTab === "addProduct" && <AddProduct />}
              {activeTab === "categorysettings" && <CategorySettingsAdmin />}
            </ContentPaper>
          </Fade>
        </Main>
      </Box>

      {/* Enhanced Confirmation Dialog with animations */}
      <Dialog
        open={openLogoutAlert}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: "24px",
            overflow: "hidden",
            background: "white",
            boxShadow: "0 15px 40px rgba(211, 47, 47, 0.2)",
            minWidth: "400px",
            transform: "scale(0.9)",
            opacity: 0,
            animation: `${keyframes`
                    from { transform: scale(0.9); opacity: 0; }
                    to { transform: scale(1); opacity: 1; }
                  `} 0.3s ease forwards`,
          },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{
            background: "linear-gradient(135deg, #d32f2f 0%, #9a0007 100%)",
            color: "white",
            fontWeight: 700,
            py: 3,
            textAlign: "center",
            fontSize: "1.5rem",
          }}
        >
          Confirm Logout
        </DialogTitle>
        <DialogContent sx={{ py: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 2,
              gap: 3,
            }}
          >
            <Avatar
              sx={{
                bgcolor: "#ffebee",
                color: "#d32f2f",
                width: 80,
                height: 80,
                animation: `${pulseAnimation} 2s ease infinite`,
              }}
            >
              <LogoutIcon fontSize="large" />
            </Avatar>
            <DialogContentText
              id="alert-dialog-description"
              sx={{
                textAlign: "center",
                color: "#4e0000",
                fontSize: "1.1rem",
                maxWidth: "350px",
              }}
            >
              Are you sure you want to logout from your account?
            </DialogContentText>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 4,
            py: 3,
            borderTop: "1px solid rgba(211, 47, 47, 0.1)",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              borderColor: "#d32f2f",
              color: "#d32f2f",
              px: 4,
              py: 1,
              "&:hover": {
                backgroundColor: "#ffebee",
                borderColor: "#d32f2f",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            color="primary"
            autoFocus
            sx={{
              px: 4,
              py: 1,
              boxShadow: "0 4px 14px rgba(211, 47, 47, 0.3)",
              "&:hover": {
                boxShadow: "0 6px 20px rgba(211, 47, 47, 0.4)",
                backgroundColor: "#9a0007",
              },
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default AdminDashboard;
